import type { AppRole, OrgRole } from '@empower-safety/shared-types';
import { useQuery } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface Capabilities {
  worker: boolean;
  monitor: boolean;
  admin: boolean;
  teamLeader: boolean;
}

export interface MeResponse {
  user_id: string;
  clerk_user_id: string;
  email: string;
  display_name: string;
  organisation_id: string | null;
  clerk_org_id: string | null;
  org_role: OrgRole | null;
  app_role: AppRole | null;
  capabilities: Capabilities;
}

// Returns true if the authenticated user is allowed to use the dashboard at
// all. Workers without monitor/admin/teamLeader status should be steered to
// the mobile app — they have no useful pages here.
export function canUseDashboard(caps: Capabilities | undefined | null): boolean {
  if (!caps) return false;
  return caps.monitor || caps.admin || caps.teamLeader;
}

export function useMe() {
  const api = useApi();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/me'),
    staleTime: 60_000,
    retry: (failureCount, err) => {
      // Don't retry auth failures — Clerk's session will refresh on its own cadence.
      const status = (err as { status?: number }).status;
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
  });
}
